/**
 * v4 File chat - "Ask about this file" scoped chat. Top of screen shows the
 * file you're talking about; below is a chat thread that ONLY knows about
 * this document. Suggested questions are file-type-aware.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Send, User, Sparkles, ArrowUpRight, Download, FileText, Mail, Landmark, BarChart3, Receipt as ReceiptIcon, Loader2, RotateCw } from 'lucide-react';
import { useV4 } from './i18n';
import { listFiles, ask, sendMessage as sendClientMessage, getFileDownloadUrl, processFile, useClient, ClientFile, ChatSource } from '@/services/api/client-auth';
import { FormattedChatMessage } from '@/components/chat/ChatRenderer';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface Msg { id: number; from: 'user' | 'ai'; text: string; forwardable?: boolean; forwardedToHuman?: boolean; sources?: ChatSource[] }

const SUGGESTED_BY_CATEGORY: Record<string, string[]> = {
  tax: [
    'What was my taxable income?',
    'How much tax did I pay?',
    'What deductions did we claim?',
    'When is the next instalment due?',
  ],
  bas: [
    'What\'s the total GST on this BAS?',
    'When does this need to be lodged?',
    'Has this been paid?',
  ],
  financials: [
    'What was my profit?',
    'How did expenses change from last period?',
    'Show me my biggest expense categories',
  ],
  letters: [
    'What does this letter mean?',
    'What do I need to do?',
    'When is the deadline?',
  ],
  receipts: [
    'Is this a business expense?',
    'What category should this be?',
  ],
  other: [
    'Summarise this document for me',
    'What are the key dates I need to know?',
  ],
};

const CAT_ICON: Record<string, any> = {
  tax: Landmark, bas: FileText, financials: BarChart3,
  letters: Mail, receipts: ReceiptIcon, other: FileText,
};

const CAT_COLOR: Record<string, { bg: string; text: string }> = {
  tax:        { bg: 'bg-rose-50 dark:bg-rose-950/40',    text: 'text-rose-700 dark:text-rose-300' },
  bas:        { bg: 'bg-amber-50 dark:bg-amber-950/40',   text: 'text-amber-700 dark:text-amber-300' },
  financials: { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-300' },
  letters:    { bg: 'bg-[var(--v4-accent-tint)]',  text: 'text-[var(--v4-accent-text)]' },
  receipts:   { bg: 'bg-teal-50 dark:bg-teal-950/40',    text: 'text-teal-700' },
  other:      { bg: 'bg-muted',  text: 'text-foreground/80' },
};

export default function FileChatPageV4() {
  const { t, dir, settings } = useV4();
  const loggedInClient = useClient();
  const firmName = loggedInClient?.tenantName || 'your accountant';
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();

  const qc = useQueryClient();

  // Poll while the file is being processed. React Query auto-refetches and
  // re-renders so the "Still processing" badge clears the moment the backend
  // marks the row 'completed'.
  const { data: files = [], isLoading: filesLoading } = useQuery({
    queryKey: ['client-files'],
    queryFn: () => listFiles(),
    staleTime: 5_000,
    refetchInterval: (q) => {
      const d = q.state.data as ClientFile[] | undefined;
      if (!d || !fileId) return false;
      const f = d.find(x => x.id === fileId);
      // Keep polling while the file is anything other than 'completed'.
      return f && f.processingStatus !== 'completed' ? 3000 : false;
    },
  });
  const file = useMemo(() => files.find(f => f.id === fileId), [files, fileId]);

  // On mount (or whenever the file row changes), if the file isn't indexed
  // yet, kick the processor. We do this once per fileId so re-renders don't
  // hammer the endpoint.
  const triggeredRef = useRef<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  useEffect(() => {
    if (!file || !fileId) return;
    if (file.processingStatus === 'completed') return;
    if (triggeredRef.current === fileId) return;
    triggeredRef.current = fileId;
    processFile(fileId).catch(() => { /* swallow; user can retry */ });
  }, [file, fileId]);

  async function retryProcess() {
    if (!fileId || retrying) return;
    setRetrying(true);
    try {
      await processFile(fileId);
      await qc.invalidateQueries({ queryKey: ['client-files'] });
      toast.success('Re-queued - this usually takes 10-30 seconds');
    } catch {
      toast.error('Could not start processing. Try again in a moment.');
    } finally {
      setRetrying(false);
    }
  }

  const [messages, setMessages] = useState<Msg[]>([
    { id: 1, from: 'ai', text: 'Hi! Ask me anything about this document.' },
  ]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);

  if (filesLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (!file) {
    return (
      <div className="space-y-3 pt-2">
        <Link to="/client/files" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t('back_to_files')}
        </Link>
        <p className="text-sm text-muted-foreground">File not found.</p>
      </div>
    );
  }

  const category = (['tax', 'bas', 'financials', 'letters', 'receipts', 'other'].includes(file.category) ? file.category : 'other');
  const Icon = CAT_ICON[category];
  const color = CAT_COLOR[category];
  const suggested = SUGGESTED_BY_CATEGORY[category] || SUGGESTED_BY_CATEGORY.other;
  const indexedReady = file.processingStatus === 'completed';

  async function send(txt: string) {
    const text = txt.trim();
    if (!text) return;
    const userMsg: Msg = { id: Date.now(), from: 'user', text };
    const prior = messages;
    setMessages(m => [...m, userMsg]);
    setInput('');
    setThinking(true);
    try {
      const history = prior
        .filter(m => m.id !== 1)
        .map(m => ({ role: (m.from === 'user' ? 'user' : 'assistant') as 'user' | 'assistant', content: m.text }));
      const reply = await ask(text, history, {
        fileId: file!.id,
        dataSource: 'documents',
        lang: settings.lang || undefined,
      });
      setMessages(m => [...m, {
        id: Date.now() + 1, from: 'ai',
        text: reply.message.content,
        forwardable: true,
        sources: reply.sources || [],
      }]);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not get an answer');
      setMessages(m => [...m, { id: Date.now() + 1, from: 'ai', text: 'Sorry, I couldn\'t answer that just now.', forwardable: true }]);
    } finally {
      setThinking(false);
    }
  }

  async function forward(id: number) {
    const aiMsg = messages.find(m => m.id === id);
    if (!aiMsg) return;
    const aiIdx = messages.findIndex(m => m.id === id);
    const userMsg = [...messages].slice(0, aiIdx).reverse().find(m => m.from === 'user');
    setMessages(m => m.map(msg => msg.id === id ? { ...msg, forwardedToHuman: true, forwardable: false } : msg));
    const body =
      `[Forwarded from AI chat about "${file!.filename}"]\n\n` +
      (userMsg ? `My question:\n${userMsg.text}\n\n` : '') +
      `What the AI said:\n${aiMsg.text}\n\n` +
      `I'd like your take on this when you have a moment.`;
    try {
      await sendClientMessage(body, file!.id);
      setMessages(m => [...m, { id: Date.now(), from: 'ai', text: t('forwarded_to_accountant') }]);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not forward');
      setMessages(m => m.map(msg => msg.id === id ? { ...msg, forwardedToHuman: false, forwardable: true } : msg));
    }
  }

  async function downloadFile() {
    try {
      const { url } = await getFileDownloadUrl(file!.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not open file');
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* File header */}
      <div className="pb-3 pt-1">
        <button onClick={() => navigate(-1)} className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground">
          <ArrowLeft className={`h-3.5 w-3.5 ${dir === 'rtl' ? 'rotate-180' : ''}`} />
          {t('back_to_files')}
        </button>
        <div className="rounded-2xl border border-border bg-card p-3">
          <div className="flex items-start gap-3">
            <div className={cn('flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl', color.bg, color.text)}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{file.filename}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {file.financialYear ? `FY ${file.financialYear} · ` : ''}{new Date(file.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })} · {(file.fileSizeBytes / 1024).toFixed(0)} KB
              </p>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <button onClick={downloadFile} className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground/80 hover:border-muted-foreground/40">
                  <Download className="h-3 w-3" />
                  {t('download')}
                </button>
                {!indexedReady && (
                  <>
                    <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 px-2 py-0.5 text-[10px] font-semibold">
                      <Loader2 className="h-2.5 w-2.5 animate-spin" /> Still processing
                    </span>
                    <button
                      onClick={retryProcess}
                      disabled={retrying}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 text-[10px] font-semibold text-foreground/80 hover:border-muted-foreground/40 disabled:opacity-50"
                    >
                      {retrying
                        ? <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        : <RotateCw className="h-2.5 w-2.5" />}
                      Retry
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 pb-2">
        {messages.map(msg => (
          <div key={msg.id} className={cn('flex', msg.from === 'user' ? 'justify-end' : 'justify-start')}>
            <div className={cn('flex flex-col gap-1.5 w-full', msg.from === 'user' ? 'items-end' : 'items-start')}>
              <div
                // `dir="auto"` lets the browser pick text direction from the
                // first strong directional character - Persian/Arabic/Urdu
                // content flips inside the bubble without affecting layout.
                dir="auto"
                className={cn(
                  'max-w-[92%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
                  msg.from === 'user'
                    ? 'bg-[var(--v4-accent)] text-white rounded-br-md'
                    : 'bg-card border border-border text-foreground rounded-bl-md',
                  dir === 'rtl' && msg.from === 'user' && 'rounded-bl-md rounded-br-2xl',
                  dir === 'rtl' && msg.from === 'ai' && 'rounded-br-md rounded-bl-2xl'
                )}
              >
                {msg.from === 'ai' ? <FormattedChatMessage content={msg.text} /> : msg.text}
              </div>
              {msg.from === 'ai' && msg.sources && msg.sources.length > 0 && (
                <div className="max-w-[92%] text-[10px] text-muted-foreground italic mt-0.5">
                  Based on {msg.sources.length} excerpt{msg.sources.length === 1 ? '' : 's'} from this file
                </div>
              )}
              {msg.forwardable && !msg.forwardedToHuman && (
                <button
                  onClick={() => forward(msg.id)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground/80 transition-colors hover:border-muted-foreground/60 hover:text-foreground"
                >
                  <User className="h-3 w-3" />
                  {t('ask_marcus_instead', { firm: firmName })}
                  <ArrowUpRight className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ))}

        {thinking && (
          <div className="flex">
            <div className="flex items-center gap-1 rounded-2xl rounded-bl-md border border-border bg-card px-3.5 py-3">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/50" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/50" style={{ animationDelay: '150ms' }} />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/50" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}

        {messages.length === 1 && (
          <div className="pt-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/60 mb-2">Try asking</p>
            <div className="flex flex-wrap gap-2">
              {suggested.map(q => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="rounded-full border border-border bg-card px-3 py-1.5 text-[12px] text-foreground/80 hover:border-muted-foreground/40 hover:text-foreground transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Composer - pinned at the bottom of the flex column (not fixed, so it
          never overlaps the bottom nav on iOS). */}
      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="shrink-0 pt-2">
        <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-1 shadow-md">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={t('type_question')}
            className="flex-1 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground/60 focus:outline-none"
          />
          <button
            type="submit"
            disabled={!input.trim()}
            aria-label={t('send')}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--v4-accent)] text-white shadow-sm transition-all hover:bg-[var(--v4-accent-hover)] disabled:bg-muted disabled:text-muted-foreground"
          >
            <Send className={cn('h-4 w-4', dir === 'rtl' && '-scale-x-100')} />
          </button>
        </div>
      </form>
    </div>
  );
}

function cannedFileAnswer(file: any, q: string): string {
  const lower = q.toLowerCase();
  const filename = file.filename.toLowerCase();

  if (file.category === 'tax' || filename.includes('tax')) {
    if (lower.includes('income') || lower.includes('taxable')) {
      return 'Your taxable income for FY ' + file.fy + ' was $187,420. After tax of $48,710 your after-tax profit was $138,710.';
    }
    if (lower.includes('deduction') || lower.includes('claim')) {
      return 'Top deductions claimed: motor vehicle ($8,420), home office (1,240 hours × $0.70 = $868), tools & equipment ($4,820), professional development ($1,200).';
    }
    if (lower.includes('paid') || lower.includes('tax')) {
      return 'Tax payable was $48,710. With the PAYG instalments already paid ($42,000), the final balance owing was $6,710. This was paid on the due date.';
    }
  }

  if (file.category === 'bas' || filename.includes('bas')) {
    if (lower.includes('gst') || lower.includes('total')) {
      return 'Total GST owing on this BAS: $4,820 (GST on sales $7,640 − GST credits $2,820). PAYG withholding: $2,400. PAYG instalment: $600. Total payable: $7,820.';
    }
    if (lower.includes('lodge') || lower.includes('due')) {
      return 'Lodgement due 28 May 2026. Marcus has prepared the draft - you will get a notification to approve it.';
    }
    if (lower.includes('paid')) {
      return 'Not yet lodged - this is a draft. Once approved, payment is due the same day as lodgement.';
    }
  }

  if (file.category === 'financials' || filename.includes('balance') || filename.includes('p&l')) {
    if (lower.includes('profit') || lower.includes('net')) {
      return 'Net profit for the period was $5,370 - that\'s 25.3% of revenue. Up 8% vs the prior period.';
    }
    if (lower.includes('expense') || lower.includes('biggest')) {
      return 'Biggest expense categories: Materials ($8,920 - 56%), Rent ($2,800 - 18%), Wages ($2,100 - 13%), Subcontractor ($1,240 - 8%).';
    }
  }

  if (file.category === 'letters') {
    if (lower.includes('mean') || lower.includes('summary')) {
      return 'In plain language: ' + file.filename.replace(/\.pdf$/, '') + ' is a routine notification. No urgent action required.';
    }
    if (lower.includes('do') || lower.includes('action')) {
      return 'No action needed from you. The deadline is handled automatically by Marcus.';
    }
    if (lower.includes('deadline') || lower.includes('when')) {
      return 'The relevant deadline mentioned in this letter is in 12 days. Your accountant is already on top of it.';
    }
  }

  return 'I have read this file but I am not sure about that specific question. Tap "Ask Marcus instead" and your accountant will look at it.';
}
